import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Loader2, Paperclip, X, ArrowLeft, ShieldAlert, ArrowUpRight, FileText,
  AlertCircle, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { STATUS_META, categoryLabel, formatWhen } from "@/lib/complaints";

const ComplaintDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [complaint, setComplaint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/complaints/${id}`);
      setComplaint(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load case");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (loading) {
    return <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }
  if (!complaint) return null;

  const closed = ["resolved", "rejected", "withdrawn"].includes(complaint.status);
  const meta = STATUS_META[complaint.status] || STATUS_META.open;
  const myRole = user?.user_id === complaint.reporter_id
    ? "reporter"
    : user?.user_id === complaint.respondent_id
      ? "respondent"
      : user?.role === "admin" ? "admin" : null;

  const uploadEvidence = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachments((a) => [...a, { path: data.path, name: file.name, content_type: file.type }]);
    } catch { toast.error("Upload failed"); }
    finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (reply.trim().length < 1) return;
    setSending(true);
    try {
      const { data } = await api.post(`/complaints/${id}/messages`, { body: reply.trim(), attachments });
      setComplaint(data);
      setReply("");
      setAttachments([]);
      toast.success("Reply sent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send reply");
    } finally { setSending(false); }
  };

  const escalate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/complaints/${id}/escalate`);
      setComplaint(data);
      toast.success("Case escalated to admin");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not escalate"); }
    finally { setBusy(false); }
  };
  const withdraw = async () => {
    if (!window.confirm("Withdraw this complaint? You won't be able to reopen it.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/complaints/${id}/withdraw`);
      setComplaint(data);
      toast.success("Case withdrawn");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not withdraw"); }
    finally { setBusy(false); }
  };

  const initials = (name) => (name || "?").slice(0, 1).toUpperCase();

  return (
    <div className="container-narrow py-10 max-w-4xl">
      <button onClick={() => navigate(-1)} className="text-sm text-muted-foreground hover:text-[#C05A3C] flex items-center gap-1 mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Back
      </button>

      <div className="flex flex-wrap items-start gap-3 mb-2">
        <Badge className={`${meta.color} text-xs`} data-testid="complaint-status">{meta.label}</Badge>
        <span className="text-xs text-muted-foreground uppercase tracking-wider">{categoryLabel(complaint.category)}</span>
        {complaint.payout_held && (
          <Badge className="bg-[#1A2024] text-[#D9923B] text-[10px]">PAYOUT HELD</Badge>
        )}
      </div>
      <h1 className="font-display text-3xl sm:text-4xl mb-2" data-testid="complaint-subject">{complaint.subject}</h1>
      <div className="text-sm text-muted-foreground mb-6">
        Opened {formatWhen(complaint.created_at)} · Last update {formatWhen(complaint.updated_at)}
      </div>

      {complaint.resolution && (
        <Card className="p-4 mb-6 bg-[#4A5D23]/5 border-[#4A5D23]/20">
          <div className="flex items-center gap-2 text-[#4A5D23] font-semibold text-sm">
            <CheckCircle2 className="w-4 h-4" /> Resolution: {complaint.resolution.replace(/_/g, " ")}
          </div>
          {complaint.resolution_notes && (
            <div className="text-sm mt-2 text-muted-foreground">{complaint.resolution_notes}</div>
          )}
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <Card className="p-5 bg-white border-black/10">
            <div className="flex items-start gap-3 mb-3">
              <Avatar className="w-9 h-9"><AvatarFallback className="bg-[#C05A3C] text-white text-sm">{initials(complaint.reporter_name)}</AvatarFallback></Avatar>
              <div>
                <div className="font-semibold text-sm">{complaint.reporter_name || complaint.reporter_email}</div>
                <div className="text-xs text-muted-foreground">Opened the case · {formatWhen(complaint.created_at)}</div>
              </div>
            </div>
            <div className="text-sm whitespace-pre-wrap font-sans-ui leading-relaxed">{complaint.description}</div>
            {complaint.attachments?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {complaint.attachments.map((a, i) => (
                  <a
                    key={i}
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${a.path}`}
                    target="_blank" rel="noreferrer"
                    className="flex items-center gap-1.5 text-xs bg-[#F3EFE9] hover:bg-[#F3EFE9]/70 rounded-md px-3 py-1.5"
                  >
                    <FileText className="w-3 h-3" /> {a.name || "evidence"} <ArrowUpRight className="w-3 h-3" />
                  </a>
                ))}
              </div>
            )}
          </Card>

          {complaint.messages?.map((m, i) => (
            <Card key={m.message_id || i} className={`p-5 border-black/10 ${m.sender_role === "admin" ? "bg-[#1A2024] text-white" : "bg-white"}`} data-testid={`complaint-msg-${i}`}>
              <div className="flex items-start gap-3 mb-2">
                <Avatar className="w-8 h-8">
                  <AvatarFallback className={m.sender_role === "admin" ? "bg-[#D9923B] text-[#1A2024]" : "bg-[#233348] text-white"}>
                    {initials(m.sender_name)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="font-semibold text-sm">
                    {m.sender_name || "User"}
                    {m.sender_role === "admin" && <Badge className="ml-2 bg-[#D9923B] text-[#1A2024] text-[10px]">ADMIN</Badge>}
                  </div>
                  <div className={`text-xs ${m.sender_role === "admin" ? "text-white/60" : "text-muted-foreground"}`}>{formatWhen(m.created_at)}</div>
                </div>
              </div>
              <div className="text-sm whitespace-pre-wrap leading-relaxed">{m.body}</div>
              {m.attachments?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {m.attachments.map((a, j) => (
                    <a
                      key={j}
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${a.path}`}
                      target="_blank" rel="noreferrer"
                      className={`flex items-center gap-1.5 text-xs rounded-md px-3 py-1.5 ${m.sender_role === "admin" ? "bg-white/10 hover:bg-white/20" : "bg-[#F3EFE9] hover:bg-[#F3EFE9]/70"}`}
                    >
                      <FileText className="w-3 h-3" /> {a.name || "evidence"}
                    </a>
                  ))}
                </div>
              )}
            </Card>
          ))}

          {!closed && myRole && (
            <Card className="p-5 bg-white border-black/10">
              <form onSubmit={sendReply} className="space-y-3" data-testid="complaint-reply-form">
                <Textarea
                  data-testid="complaint-reply-input"
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Type your reply…"
                  rows={4}
                />
                {attachments.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {attachments.map((a, i) => (
                      <div key={i} className="flex items-center gap-1.5 bg-[#F3EFE9] rounded-md px-2 py-1 text-xs">
                        <Paperclip className="w-3 h-3" />
                        <span className="truncate max-w-[160px]">{a.name}</span>
                        <button type="button" onClick={() => setAttachments((arr) => arr.filter((_, j) => j !== i))}>
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="reply-attach-btn">
                      {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Paperclip className="w-3.5 h-3.5 mr-1" />} Attach
                    </Button>
                    <input ref={fileRef} type="file" hidden accept="image/*,application/pdf" onChange={(e) => e.target.files?.[0] && uploadEvidence(e.target.files[0])} />
                  </div>
                  <Button type="submit" disabled={sending || !reply.trim()} className="bg-[#C05A3C] hover:bg-[#D46B4E] text-white rounded-md" data-testid="send-reply-btn">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reply"}
                  </Button>
                </div>
              </form>
            </Card>
          )}
        </div>

        <aside className="space-y-4">
          <Card className="p-5 bg-white border-black/10">
            <div className="label-overline text-muted-foreground mb-3">Parties</div>
            <div className="space-y-3 text-sm">
              <div>
                <div className="text-xs text-muted-foreground">Reporter</div>
                <div className="font-semibold">{complaint.reporter_name || "—"}</div>
                <div className="text-xs text-muted-foreground truncate">{complaint.reporter_email}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Respondent</div>
                <div className="font-semibold">{complaint.respondent_name || complaint.respondent_email || "Unassigned"}</div>
                {complaint.respondent_email && complaint.respondent_name && (
                  <div className="text-xs text-muted-foreground truncate">{complaint.respondent_email}</div>
                )}
              </div>
              {complaint.listing_id && (
                <div>
                  <div className="text-xs text-muted-foreground">Listing</div>
                  <Link to={`/listing/${complaint.listing_id}`} className="text-[#C05A3C] underline text-sm">
                    {complaint.listing_title || complaint.listing_id}
                  </Link>
                </div>
              )}
              {complaint.payment_id && (
                <div>
                  <div className="text-xs text-muted-foreground">Payment</div>
                  <div className="font-mono text-xs truncate">{complaint.payment_id}</div>
                  {complaint.payout_held && (
                    <div className="text-xs text-[#D9923B] flex items-center gap-1 mt-1">
                      <AlertCircle className="w-3 h-3" /> Payout on hold
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>

          {!closed && myRole && myRole !== "admin" && (
            <Card className="p-5 bg-[#F3EFE9] border-transparent">
              <div className="label-overline text-muted-foreground mb-3">Actions</div>
              {complaint.status !== "escalated" && (
                <Button onClick={escalate} disabled={busy} variant="outline" className="w-full mb-2 rounded-md" data-testid="escalate-btn">
                  <ShieldAlert className="w-4 h-4 mr-2" /> Escalate to admin
                </Button>
              )}
              {myRole === "reporter" && (
                <Button onClick={withdraw} disabled={busy} variant="ghost" className="w-full text-muted-foreground" data-testid="withdraw-btn">
                  <X className="w-4 h-4 mr-2" /> Withdraw case
                </Button>
              )}
            </Card>
          )}

          {user?.role === "admin" && !closed && (
            <Card className="p-5 bg-[#1A2024] text-white border-transparent">
              <div className="label-overline text-white/60 mb-3">Admin actions</div>
              <Button onClick={() => navigate(`/admin/complaints?id=${id}`)} className="w-full bg-[#D9923B] hover:bg-[#D9923B]/90 text-[#1A2024] font-semibold rounded-md" data-testid="admin-resolve-link">
                Resolve in admin panel →
              </Button>
            </Card>
          )}
        </aside>
      </div>
    </div>
  );
};

export default ComplaintDetail;
